// THƯ VIỆN SẢN PHẨM (docs/thiet-ke-thu-vien-san-pham.md): danh sách sản phẩm đã lưu + trang tạo/sửa một sản phẩm. Đọc tệp NGAY TRÊN TRÌNH DUYỆT
// bằng src/shared/tai-lieu.ts (giống plugin; thêm PDF có lớp chữ), xem trước chữ trước khi lưu; ảnh thu nhỏ ≤ 1024 px bằng canvas rồi mới gửi.
// "AI tóm tắt" chạy trên máy chủ (lời nhắc của plugin) → 5 ô sửa tay được. Mở từ kịch bản (?kb=<id>) → lưu xong quay lại kịch bản và tự chọn.
// Mọi chữ dựng bằng textContent (ký tự đặc biệt hiện dạng chữ).
import { api, ApiError, buildQuery, errMsg } from "../api";
import { busy, clear, confirmBox, gen, h, toast } from "../dom";
import { danhDauVeLai, diToi, nganDangMo } from "../ngan-truot";
import { fmtTime } from "../format";
import { store } from "../store";
import { docTaiLieu, kiemTepTruoc, DUOI_TAI_LIEU_BOSS, TAI_LIEU_TOI_DA_BYTE } from "../../../../src/shared/tai-lieu";
import { gopTaiLieu, SP_TEN_TOI_DA, type SanPham, type TepSanPham } from "../../../../src/shared/san-pham";
import { HO_SO_TAI_LIEU_TOI_DA, type TomTatSanPham } from "../../../../src/shared/ho-so";
import { ANH_HO_SO_TOI_DA } from "../../../../src/shared/anh";
import { theSpMauDaPhat, theSpMauNguon, theSpMauTuSuper } from "./san-pham-mau";

const ANH_BYTE = 290_000; // = ANH_SP_BYTE_TOI_DA của máy chủ
interface Dong { id: string; ownerId: string; ownerName: string | null; ten: string; soTep: number; soAnh: number; coTomTat: boolean; tomTatNgan: string; phienBan: number; suaLuc: string }
type AnhCt = SanPham["anh"][number] & { url: string };
export interface ChiTietSp extends Omit<SanPham, "anh"> { anh: AnhCt[]; kichBan: { id: string; ten: string; phienBanSp: number }[] }

const dem = (s: string) => Array.from(s).length;
const kb = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const maYeuCau = (): string => `sp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const dongRa = (s: string): string[] => s.split("\n").map((x) => x.trim()).filter(Boolean);

// ---------------- ảnh: thu nhỏ ≤ 1024 px, JPEG, ≤ 290 KB ----------------
export async function thuNhoAnh(f: File): Promise<string> {
  if (/svg/i.test(f.type) || /\.svg$/i.test(f.name)) throw new Error(`'${f.name}': không nhận ảnh SVG — chọn ảnh JPEG/PNG/WebP`);
  let bmp: ImageBitmap;
  try { bmp = await createImageBitmap(f); } catch { throw new Error(`'${f.name}' không phải ảnh đọc được (tệp hỏng hoặc sai loại)`); }
  const k = Math.min(1, 1024 / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * k)), ht = Math.max(1, Math.round(bmp.height * k));
  const c = document.createElement("canvas");
  c.width = w; c.height = ht;
  const x = c.getContext("2d")!;
  x.fillStyle = "#fff"; x.fillRect(0, 0, w, ht);
  x.drawImage(bmp, 0, 0, w, ht);
  bmp.close();
  for (const q of [0.85, 0.72, 0.6, 0.45]) {
    const u = c.toDataURL("image/jpeg", q);
    if (((u.length - u.indexOf(",") - 1) * 3) / 4 <= ANH_BYTE) return u;
  }
  throw new Error(`'${f.name}': ảnh quá chi tiết, thu nhỏ vẫn trên ${Math.round(ANH_BYTE / 1024)} KB — cắt bớt ảnh rồi thử lại`);
}
const laAnh = (f: File): boolean => f.type.startsWith("image/") || /\.(jpe?g|png|webp|gif|bmp|svg)$/i.test(f.name);

// =============== danh sách ===============
export async function renderSanPhamDs(root: HTMLElement): Promise<() => void> {
  const alive = gen("san-pham");
  const laSuper = store.user?.role === "super";
  root.append(h("h1", {}, "Sản phẩm"));
  const nhapTep = h("input", { type: "file", accept: ".json,application/json", hidden: true, "data-testid": "sp-nhap-tep" });
  const nhap = h("button", { type: "button", "data-testid": "sp-nhap", title: "Nhập các hồ sơ sản phẩm đã có trên plugin (tệp JSON do nút “Xuất JSON cho Boss” ở tab Sản phẩm của plugin tạo ra)" }, "Nhập hồ sơ từ plugin");
  const ketQuaNhap = h("div", { hidden: true, "data-testid": "sp-nhap-kq" });
  root.append(h("div", { class: "card" },
    h("p", { class: "small muted" }, "Tải tài liệu (.txt, .md, .doc, .docx, .pdf) và ảnh sản phẩm MỘT lần, bấm AI tóm tắt, thêm quy tắc — rồi chọn sản phẩm trong từng kịch bản. AI soạn nội dung chỉ được nói điều có trong sản phẩm; bot đối chiếu số/giá với tài liệu đầy đủ."),
    h("div", { class: "row wrap" }, h("a", { class: "btn pri", href: "#/san-pham/moi", "data-testid": "sp-them", title: "Tạo sản phẩm mới: tên, tệp tài liệu, ảnh, quy tắc" }, "＋ Thêm sản phẩm"), nhap, nhapTep),
    ketQuaNhap));
  root.append(laSuper ? theSpMauDaPhat(alive) : theSpMauTuSuper(alive)); // Sản phẩm mẫu của Super (san-pham-mau.ts)
  nhap.addEventListener("click", () => nhapTep.click());
  nhapTep.addEventListener("change", () => void busy(nhap, "Đang nhập…", async () => {
    const f = nhapTep.files?.[0];
    nhapTep.value = "";
    if (!f) return;
    clear(ketQuaNhap); ketQuaNhap.hidden = false;
    try {
      if (f.size > 7 * 1024 * 1024) throw new Error("Tệp quá lớn (tối đa 7 MB)");
      let j: unknown;
      try { j = JSON.parse(await f.text()); } catch { throw new Error("Tệp hỏng: không đọc được JSON — chọn tệp do nút “Xuất JSON cho Boss” của plugin tạo ra"); }
      const r = await api<{ taoMoi: { id: string; ten: string }[]; loi: { ten: string; loi: string }[] }>("POST", "/api/san-pham/nhap", j);
      ketQuaNhap.append(h("div", { class: r.taoMoi.length ? "ok-box" : "warn-box" }, `Đã nhập ${r.taoMoi.length} sản phẩm${r.loi.length ? `, ${r.loi.length} hồ sơ lỗi` : ""}.`),
        ...r.loi.map((l) => h("div", { class: "err-box small" }, `“${l.ten}”: ${l.loi}`)));
      before = null; clear(ds); await tai();
    } catch (e) { ketQuaNhap.append(h("div", { class: "err-box" }, errMsg(e))); }
  }));
  const tim = h("input", { type: "search", placeholder: "Tìm theo tên sản phẩm", "data-testid": "sp-tim", maxlength: 80 });
  const ds = h("div", { "data-testid": "sp-ds" });
  const them = h("button", { type: "button", hidden: true, "data-testid": "sp-xem-them", title: "Tải thêm các sản phẩm cũ hơn" }, "Xem thêm");
  root.append(h("div", { class: "card" }, h("div", { class: "row between wrap" }, h("h3", {}, laSuper ? "Sản phẩm của mọi Boss" : "Sản phẩm đã lưu"), tim), ds, h("div", { class: "row" }, them)));
  let before: string | null = null;
  let hen: number | undefined;
  const tai = async (): Promise<void> => {
    const r = await api<{ items: Dong[]; nextBefore: string | null }>("GET", `/api/san-pham${buildQuery({ limit: 50, before, q: tim.value.trim() || null })}`);
    if (!alive()) return;
    if (!before && !r.items.length) ds.append(h("p", { class: "empty", "data-testid": "sp-trong" }, tim.value.trim() ? "Không có sản phẩm nào khớp tên này." : "Chưa có sản phẩm nào — bấm “＋ Thêm sản phẩm”, tải tài liệu và ảnh một lần, rồi chọn trong kịch bản."));
    for (const s of r.items) {
      ds.append(h("a", { class: "kb-dong sp-dong", href: `#/san-pham/${encodeURIComponent(s.id)}`, "data-testid": "sp-dong", dataset: { id: s.id } },
        h("span", { class: "kb-dong-ten", "data-testid": "sp-dong-ten" }, s.ten),
        h("span", { class: `tag ${s.coTomTat ? "ok" : "warn"}` }, s.coTomTat ? "đã tóm tắt" : "chưa tóm tắt"),
        h("span", { class: "kb-dong-phu small muted" }, `${s.soTep} tệp · ${s.soAnh} ảnh · bản ${s.phienBan} · sửa ${fmtTime(s.suaLuc)}${laSuper && s.ownerName ? ` · ${s.ownerName}` : ""}`),
        s.tomTatNgan ? h("span", { class: "sp-dong-tt small" }, s.tomTatNgan) : null));
    }
    before = r.nextBefore;
    them.hidden = !before;
  };
  tim.addEventListener("input", () => { window.clearTimeout(hen); hen = window.setTimeout(() => { before = null; clear(ds); void tai().catch((e) => ds.append(h("div", { class: "err-box" }, errMsg(e)))); }, 250); });
  them.addEventListener("click", () => void busy(them, "Đang tải…", tai));
  await tai().catch((e) => ds.append(h("div", { class: "err-box" }, `Không tải được danh sách: ${errMsg(e)}`)));
  return () => window.clearTimeout(hen);
}

// =============== một sản phẩm (tạo / sửa) ===============
export async function renderSanPham(root: HTMLElement, params: Record<string, string>, query: URLSearchParams): Promise<() => void> {
  const alive = gen("san-pham");
  const id0 = params.id ?? "moi";
  const veKichBan = query.get("kb") && /^[a-z0-9]{6,24}$/.test(query.get("kb")!) ? query.get("kb")! : null;
  let d: ChiTietSp | null = id0 === "moi" ? null : await api<ChiTietSp>("GET", `/api/san-pham/${encodeURIComponent(id0)}`);
  if (!alive()) return () => {};
  let yeuCauId = maYeuCau();

  // trạng thái form
  let tep: TepSanPham[] = d ? d.tep.map((t) => ({ ...t })) : [];
  let anhCu: AnhCt[] = d ? [...d.anh] : [];
  let anhMoi: string[] = [];
  // Chú thích ảnh (04/10/2026 — kho ảnh cho bình luận kèm ảnh: AI của bot chọn ảnh hợp câu theo chú thích). Ảnh cũ: theo mã; ảnh mới: theo vị trí.
  const moTaCu = new Map<string, string>((d?.anh ?? []).map((a) => [a.id, a.moTa ?? ""]));
  let moTaMoi: string[] = [];
  let tomTatSua = false;
  let dangDoc = 0;

  const tieuDe = h("h1", {}, d ? "Sửa sản phẩm" : "Thêm sản phẩm");
  root.append(tieuDe);
  if (veKichBan) root.append(h("div", { class: "warn-box", "data-testid": "sp-tu-kich-ban" }, "Đang thêm sản phẩm cho một kịch bản — lưu xong sẽ quay lại kịch bản và tự chọn sản phẩm này."));
  const ten = h("input", { maxlength: SP_TEN_TOI_DA * 2, value: d?.ten ?? "", placeholder: "Ví dụ: Mepo9 — phần mềm dựng video", "data-testid": "sp-ten", title: "Tên sản phẩm/dịch vụ (tối đa 80 ký tự)" });

  // ---- tệp ----
  const chonTep = h("input", { type: "file", multiple: true, accept: [...DUOI_TAI_LIEU_BOSS, "image/*"].join(","), "data-testid": "sp-chon-tep", class: "sp-an-tep" });
  const nutChon = h("button", { type: "button", "data-testid": "sp-chon", title: "Chọn một hoặc nhiều tệp tài liệu (.txt .md .doc .docx .pdf) và ảnh sản phẩm" }, "Chọn tệp hoặc ảnh…");
  const vung = h("div", { class: "sp-tha", "data-testid": "sp-tha", tabindex: "0", title: "Kéo thả tệp tài liệu hoặc ảnh vào đây" },
    h("p", {}, "Kéo thả tệp tài liệu (.txt, .md, .doc, .docx, .pdf) hoặc ảnh vào đây — hoặc ", nutChon),
    h("p", { class: "small muted" }, `Đọc ngay trên máy bạn (không gửi tệp gốc đi đâu); mỗi tệp tối đa ${TAI_LIEU_TOI_DA_BYTE / 1048576} MB; tối đa ${ANH_HO_SO_TOI_DA} ảnh (tự thu nhỏ ≤ 1024 px). PDF ảnh quét: hãy tải ảnh của trang.`), chonTep);
  const tepDs = h("ul", { class: "sp-tep-ds", "data-testid": "sp-tep-ds" });
  const tepLoi = h("div", { "data-testid": "sp-tep-loi" });
  const dangDocEl = h("p", { class: "small muted", hidden: true, "data-testid": "sp-dang-doc" }, "Đang đọc tệp…");
  const dan = h("textarea", { rows: 5, "data-testid": "sp-dan", placeholder: "Dán thêm chữ về sản phẩm: là gì, làm được gì, cho ai, giá (nếu muốn AI được nói)…", title: "Chữ dán thêm — ghép sau chữ của các tệp" });
  dan.value = d?.taiLieuDan ?? "";
  const demTl = h("span", { class: "small muted", "data-testid": "sp-dem" });
  const anhDs = h("div", { class: "sp-anh-ds", "data-testid": "sp-anh-ds" });

  // ---- quy tắc / ghi nhớ ----
  const quyTac = h("textarea", { rows: 3, "data-testid": "sp-quy-tac", placeholder: "Mỗi dòng một quy tắc, ví dụ: chỉ nhắc app ở cuối câu; không nói giá", title: "AI soạn nội dung luôn theo các quy tắc này" });
  quyTac.value = (d?.quyTac ?? []).join("\n");
  const ghiNho = h("textarea", { rows: 2, "data-testid": "sp-ghi-nho", placeholder: "Mỗi dòng một điều đã rút ra, ví dụ: khách hỏi nhiều về bản miễn phí" });
  ghiNho.value = (d?.ghiNho ?? []).join("\n");

  // ---- tóm tắt ----
  const ttTomTat = h("textarea", { rows: 3, "data-testid": "sp-tt-tom-tat", placeholder: "2–4 câu: sản phẩm là gì, làm được gì" });
  const ttDiem = h("textarea", { rows: 3, "data-testid": "sp-tt-diem", placeholder: "Mỗi dòng một điểm nổi bật" });
  const ttKhach = h("input", { "data-testid": "sp-tt-khach", placeholder: "Khách mục tiêu" });
  const ttKhongNoi = h("textarea", { rows: 2, "data-testid": "sp-tt-khong-noi", placeholder: "Mỗi dòng một điều KHÔNG được nói" });
  const ttTuAnh = h("textarea", { rows: 2, "data-testid": "sp-tt-tu-anh", placeholder: "Mỗi dòng một điều AI đọc được từ ảnh (giá, chữ, tên gói…)" });
  const ttGhiChu = h("div", { "data-testid": "sp-tt-ghi-chu" });
  const ttTrangThai = h("p", { class: "small", "data-testid": "sp-tt-trang-thai" });
  const dienTomTat = (t: TomTatSanPham | null): void => {
    ttTomTat.value = t?.tom_tat ?? ""; ttDiem.value = (t?.diem_noi_bat ?? []).join("\n"); ttKhach.value = t?.khach_muc_tieu ?? "";
    ttKhongNoi.value = (t?.khong_noi ?? []).join("\n"); ttTuAnh.value = (t?.tu_anh ?? []).join("\n");
    tomTatSua = false;
  };
  dienTomTat(d?.tomTat ?? null);
  for (const o of [ttTomTat, ttDiem, ttKhach, ttKhongNoi, ttTuAnh]) o.addEventListener("input", () => { tomTatSua = true; veTrangThai(); });
  const tomTatTuO = (): TomTatSanPham | null => {
    const t = ttTomTat.value.trim();
    if (!t) return null;
    const x: TomTatSanPham = { tom_tat: t, diem_noi_bat: dongRa(ttDiem.value), khach_muc_tieu: ttKhach.value.trim(), khong_noi: dongRa(ttKhongNoi.value) };
    const a = dongRa(ttTuAnh.value);
    if (a.length) x.tu_anh = a;
    return x;
  };

  // Lỗi của ô tên hiện NGAY DƯỚI ô tên (09/10/2026): trước đây chỉ nhảy con trỏ vào ô, câu lỗi nằm ở hộp lỗi chung phía dưới nên người dùng không thấy gì.
  const tenLoi = h("p", { class: "small err", hidden: true, role: "alert", "data-testid": "sp-ten-loi" });
  const baoLoiTen = (m: string | null): void => { tenLoi.textContent = m ?? ""; tenLoi.hidden = !m; if (m) ten.setAttribute("aria-invalid", "true"); else ten.removeAttribute("aria-invalid"); };
  ten.addEventListener("input", () => baoLoiTen(null));
  const luuBtn = h("button", { type: "button", class: "pri", "data-testid": "sp-luu", title: "Lưu sản phẩm vào thư viện (máy chủ kiểm lại)" }, veKichBan ? "Lưu và quay lại kịch bản" : "Lưu sản phẩm");
  const ttBtn = h("button", { type: "button", "data-testid": "sp-tom-tat", title: "AI đọc tài liệu + ảnh rồi tóm tắt (lưu sản phẩm trước nếu có thay đổi)" }, "AI tóm tắt");
  const xoaBtn = h("button", { type: "button", class: "danger mini", "data-testid": "sp-xoa", hidden: !d, title: "Xoá sản phẩm khỏi thư viện" }, "Xoá sản phẩm");
  const loi = h("div", { class: "err-box", hidden: true, "data-testid": "sp-loi" });
  const kbDung = h("div", { "data-testid": "sp-kb-dung" });

  const taiLieu = (): string => gopTaiLieu(tep, dan.value);
  function veTrangThai(): void {
    const n = dem(taiLieu());
    demTl.textContent = `Tài liệu: ${n.toLocaleString("vi-VN")}/${HO_SO_TAI_LIEU_TOI_DA.toLocaleString("vi-VN")} ký tự (AI tóm tắt đọc 12.000 ký tự đầu; bot đối chiếu số/giá với toàn bộ)`;
    demTl.classList.toggle("err", n > HO_SO_TAI_LIEU_TOI_DA);
    const daDoi = !!d && (taiLieu() !== d.taiLieu || anhMoi.length > 0 || anhCu.length !== d.anh.length);
    ttTrangThai.textContent = !d ? "Lưu sản phẩm rồi bấm “AI tóm tắt” (hoặc tự gõ tóm tắt bên dưới)." : daDoi && !tomTatSua ? "Tài liệu/ảnh đã đổi — tóm tắt cũ sẽ bị bỏ khi lưu; bấm “AI tóm tắt” để làm lại." : d.tomTat ? `AI tóm tắt lúc ${fmtTime(d.tomTatLuc ?? d.suaLuc)}${tomTatSua ? " — bạn đã sửa tay (lưu để giữ)" : ""}` : "Chưa có tóm tắt — bấm “AI tóm tắt”.";
    ttTrangThai.className = `small ${daDoi && !tomTatSua ? "warn" : "muted"}`;
    clear(ttGhiChu);
    if (d?.tomTatGhiChu) ttGhiChu.append(h("div", { class: "warn-box", "data-testid": "sp-tt-canh-bao" }, `⚠ ${d.tomTatGhiChu}`));
  }
  function veTep(): void {
    clear(tepDs);
    tep.forEach((t, i) => {
      const bo = h("button", { type: "button", class: "mini", "data-testid": "sp-tep-bo", title: `Bỏ tệp “${t.ten}” khỏi tài liệu`, "aria-label": `Bỏ tệp ${t.ten}` }, "Bỏ");
      bo.addEventListener("click", () => { tep = tep.filter((_, j) => j !== i); veTep(); });
      tepDs.append(h("li", { class: "sp-tep", "data-testid": "sp-tep" },
        h("div", { class: "row between wrap" }, h("b", { class: "sp-tep-ten", "data-testid": "sp-tep-ten" }, t.ten), h("span", { class: "small muted" }, `${t.kichThuoc ? `${kb(t.kichThuoc)} · ` : ""}${dem(t.chu).toLocaleString("vi-VN")} ký tự`), bo),
        h("details", { class: "sp-xem" }, h("summary", { "data-testid": "sp-xem-chu" }, "Xem chữ đã đọc"), h("pre", { class: "sp-chu", "data-testid": "sp-chu" }, t.chu.length > 20_000 ? `${t.chu.slice(0, 20_000)}\n… (còn ${(t.chu.length - 20_000).toLocaleString("vi-VN")} ký tự)` : t.chu))));
    });
    veTrangThai();
  }
  function veAnh(): void {
    clear(anhDs);
    const o = (src: string, nhan: string, bo: () => void, moTa: string, datMoTa: (v: string) => void) => {
      const b = h("button", { type: "button", class: "mini", "data-testid": "sp-anh-bo", "aria-label": `Bỏ ${nhan}`, title: "Bỏ ảnh này" }, "Bỏ");
      b.addEventListener("click", () => { bo(); veAnh(); });
      const mt = h("input", { value: moTa, maxlength: 120, "data-testid": "sp-anh-mo-ta", "aria-label": `Chú thích ${nhan}`, placeholder: "Chú thích (không bắt buộc)", title: "Chú thích ngắn: ảnh này là gì (giao diện, bảng giá, ảnh thật…) — bot chọn ảnh hợp câu bình luận khi gửi kèm ảnh" }) as HTMLInputElement;
      mt.addEventListener("input", () => datMoTa(mt.value));
      return h("figure", { class: "sp-anh", "data-testid": "sp-anh" }, h("img", { src, alt: nhan }), h("figcaption", {}, mt, b));
    };
    anhCu.forEach((a, i) => anhDs.append(o(a.url, `ảnh ${i + 1}`, () => { anhCu = anhCu.filter((x) => x.id !== a.id); }, moTaCu.get(a.id) ?? a.moTa ?? "", (v) => moTaCu.set(a.id, v))));
    anhMoi.forEach((u, i) => anhDs.append(o(u, `ảnh mới ${i + 1}`, () => { anhMoi = anhMoi.filter((_, j) => j !== i); moTaMoi = moTaMoi.filter((_, j) => j !== i); }, moTaMoi[i] ?? "", (v) => { moTaMoi[i] = v; })));
    if (!anhCu.length && !anhMoi.length) anhDs.append(h("p", { class: "small muted" }, "Chưa có ảnh. Ảnh bảng giá / giao diện / tờ rơi giúp AI biết thêm điều tài liệu chưa ghi."));
    veTrangThai();
  }
  async function nhanTep(files: File[]): Promise<void> {
    clear(tepLoi);
    const baoLoi = (m: string) => tepLoi.append(h("div", { class: "err-box small", "data-testid": "sp-tep-loi-dong" }, `✗ ${m}`));
    dangDoc++; dangDocEl.hidden = false;
    try {
      for (const f of files) {
        try {
          if (laAnh(f) && !/\.(pdf|docx?|txt|md)$/i.test(f.name)) {
            if (anhCu.length + anhMoi.length >= ANH_HO_SO_TOI_DA) { baoLoi(`'${f.name}': đã đủ ${ANH_HO_SO_TOI_DA} ảnh — bỏ bớt ảnh cũ trước`); continue; }
            if (f.size > 20 * 1024 * 1024) { baoLoi(`'${f.name}' quá lớn (${kb(f.size)}) — tối đa 20 MB trước khi thu nhỏ`); continue; }
            anhMoi.push(await thuNhoAnh(f));
            if (!alive()) return;
            veAnh();
            continue;
          }
          kiemTepTruoc(f.name, f.size, { pdf: true });
          const chu = await docTaiLieu(f.name, new Uint8Array(await f.arrayBuffer()), { pdf: true });
          if (!alive()) return;
          const t: TepSanPham = { ten: f.name, loai: (/\.[^.]+$/.exec(f.name)?.[0] ?? "").toLowerCase(), kichThuoc: f.size, chu };
          const cu = tep.findIndex((x) => x.ten === f.name);
          if (cu >= 0) tep[cu] = t; else tep.push(t); // chọn lại cùng tên = thay bản cũ
          veTep();
        } catch (e) { baoLoi(errMsg(e)); }
      }
    } finally { dangDoc--; dangDocEl.hidden = dangDoc > 0; }
  }
  chonTep.addEventListener("change", () => { const f = [...(chonTep.files ?? [])]; chonTep.value = ""; void nhanTep(f); });
  nutChon.addEventListener("click", () => chonTep.click());
  vung.addEventListener("dragover", (e) => { e.preventDefault(); vung.classList.add("keo"); });
  vung.addEventListener("dragleave", () => vung.classList.remove("keo"));
  vung.addEventListener("drop", (e) => { e.preventDefault(); vung.classList.remove("keo"); void nhanTep([...(e.dataTransfer?.files ?? [])]); });
  dan.addEventListener("input", veTrangThai);

  function veKbDung(): void {
    clear(kbDung);
    if (!d?.kichBan.length) return;
    kbDung.append(h("div", { class: "card" }, h("h3", {}, `Kịch bản đang dùng (${d.kichBan.length})`),
      h("p", { class: "small muted" }, "Kịch bản giữ bản chụp sản phẩm lúc chọn — sửa ở đây xong, mở kịch bản bấm “Dùng bản mới” (lần chạy sau dùng bản mới)."),
      h("ul", {}, ...d.kichBan.map((k) => h("li", {}, h("a", { href: `#/kich-ban/${encodeURIComponent(k.id)}` }, k.ten), h("span", { class: "small muted" }, ` — đang dùng bản ${k.phienBanSp}${k.phienBanSp < d!.phienBan ? ` (thư viện đã có bản ${d!.phienBan})` : ""}`))))));
  }

  const baoLoi = (m: string | null) => { loi.textContent = m ?? ""; loi.hidden = !m; if (m) loi.scrollIntoView({ block: "nearest" }); };
  /** Lưu (tạo hoặc sửa). Trả bản chi tiết mới, hoặc null khi lỗi (đã báo). */
  async function luu(): Promise<ChiTietSp | null> {
    baoLoi(null);
    if (dangDoc > 0) { baoLoi("Đang đọc tệp — chờ đọc xong rồi lưu"); return null; }
    baoLoiTen(null);
    const tenGon = ten.value.trim();
    if (!tenGon) { baoLoi("Sản phẩm cần có tên"); baoLoiTen("Sản phẩm cần có tên — không để trống hoặc chỉ toàn dấu cách."); ten.focus(); return null; }
    if (dem(tenGon) > SP_TEN_TOI_DA) { baoLoiTen(`Tên sản phẩm tối đa ${SP_TEN_TOI_DA} ký tự (đang ${dem(tenGon)} ký tự) — rút ngắn rồi lưu lại.`); ten.focus(); return null; }
    if (dem(taiLieu()) > HO_SO_TAI_LIEU_TOI_DA) { baoLoi(`Tài liệu quá dài (tối đa ${HO_SO_TAI_LIEU_TOI_DA.toLocaleString("vi-VN")} ký tự) — bỏ bớt tệp hoặc cắt gọn`); return null; }
    const chung = { ten: ten.value, taiLieuDan: dan.value, tep, quyTac: dongRa(quyTac.value), ghiNho: dongRa(ghiNho.value) };
    try {
      if (!d) {
        d = await api<ChiTietSp>("POST", "/api/san-pham", { ...chung, anh: anhMoi, moTaAnh: anhMoi.map((_, i) => moTaMoi[i] ?? ""), ...(tomTatSua ? { tomTat: tomTatTuO() } : {}), yeuCauId });
      } else {
        d = await api<ChiTietSp>("PUT", `/api/san-pham/${encodeURIComponent(d.id)}`, { ...chung, phienBan: d.phienBan, anhGiu: anhCu.map((a) => a.id), anhMoi, moTaAnh: [...anhCu.map((a) => moTaCu.get(a.id) ?? a.moTa ?? ""), ...anhMoi.map((_, i) => moTaMoi[i] ?? "")], ...(tomTatSua ? { tomTat: tomTatTuO() } : {}) });
      }
      yeuCauId = maYeuCau();
      return d;
    } catch (e) {
      baoLoi(errMsg(e));
      if (e instanceof ApiError && e.code === "conflict" && d) toast("Sản phẩm vừa đổi ở nơi khác — tải lại trang để xem bản mới", "warn", 7000);
      return null;
    }
  }
  const sauLuu = (moi: ChiTietSp, laMoi: boolean): void => {
    tep = moi.tep.map((t) => ({ ...t })); anhCu = [...moi.anh]; anhMoi = []; moTaMoi = []; moTaCu.clear(); for (const a of moi.anh) moTaCu.set(a.id, a.moTa ?? "");
    dienTomTat(moi.tomTat); veTep(); veAnh(); veKbDung(); xoaBtn.hidden = false;
    tieuDe.textContent = "Sửa sản phẩm";
    if (nganDangMo()) danhDauVeLai(); // đang sửa trong ngăn: đóng ngăn thì vẽ lại danh sách bên dưới (tên / ảnh mới)
    if (laMoi) history.replaceState(null, "", `#/san-pham/${encodeURIComponent(moi.id)}${veKichBan ? `?kb=${veKichBan}` : ""}`);
  };
  luuBtn.addEventListener("click", () => void busy(luuBtn, "Đang lưu…", async () => {
    const laMoi = !d;
    const moi = await luu();
    if (!moi || !alive()) return;
    toast("Đã lưu sản phẩm");
    if (veKichBan) { diToi(`/kich-ban/${encodeURIComponent(veKichBan)}?chonSp=${encodeURIComponent(moi.id)}`); return; }
    sauLuu(moi, laMoi);
  }));
  ttBtn.addEventListener("click", () => void busy(ttBtn, "AI đang đọc…", async () => {
    const laMoi = !d;
    const daDoi = !d || taiLieu() !== d.taiLieu || anhMoi.length > 0 || anhCu.length !== d.anh.length || ten.value.trim() !== d.ten || tomTatSua || quyTac.value !== d.quyTac.join("\n") || ghiNho.value !== d.ghiNho.join("\n");
    if (daDoi) { const moi = await luu(); if (!moi || !alive()) return; sauLuu(moi, laMoi); }
    try {
      d = await api<ChiTietSp>("POST", `/api/san-pham/${encodeURIComponent(d!.id)}/tom-tat`, { phienBan: d!.phienBan });
      if (!alive()) return;
      dienTomTat(d.tomTat); veTrangThai();
      toast(d.tomTatGhiChu ? `AI đã tóm tắt — ${d.tomTatGhiChu}` : "AI đã tóm tắt — xem và sửa nếu cần", d.tomTatGhiChu ? "warn" : "info", 7000);
    } catch (e) { baoLoi(errMsg(e)); }
  }));
  xoaBtn.addEventListener("click", () => void (async () => {
    if (!d) return;
    const dung = d.kichBan;
    const ok = await confirmBox({ title: "Xoá sản phẩm?", danger: true, okText: "Xoá",
      text: dung.length ? `“${d.ten}” đang được ${dung.length} kịch bản dùng: ${dung.map((k) => `“${k.ten}”`).join(", ")}. Xoá thì các kịch bản đó vẫn chạy bằng bản đã chụp, nhưng không cập nhật được từ thư viện nữa.` : `Xoá “${d.ten}” khỏi thư viện. Không hoàn tác được.` });
    if (!ok) return;
    try { await api("DELETE", `/api/san-pham/${encodeURIComponent(d.id)}${dung.length ? "?dongY=1" : ""}`); toast("Đã xoá sản phẩm"); diToi("/san-pham"); }
    catch (e) { baoLoi(errMsg(e)); }
  })());

  root.append(
    h("div", { class: "card" }, h("label", { class: "f" }, h("span", {}, "Tên sản phẩm"), ten), tenLoi, d && store.user?.role === "super" && d.ownerName ? h("p", { class: "small muted" }, `Của Boss: ${d.ownerName}`) : null),
    h("div", { class: "card" }, h("h3", {}, "Tài liệu"), vung, dangDocEl, tepLoi, tepDs, h("label", { class: "f" }, h("span", {}, "Dán thêm chữ"), dan), h("div", { class: "row end" }, demTl)),
    h("div", { class: "card" }, h("h3", {}, `Ảnh sản phẩm (tối đa ${ANH_HO_SO_TOI_DA})`), anhDs),
    h("div", { class: "card", "data-testid": "sp-tt-card" }, h("div", { class: "row between wrap" }, h("h3", {}, "Tóm tắt cho AI"), ttBtn), ttTrangThai, ttGhiChu,
      h("label", { class: "f" }, h("span", {}, "Tóm tắt"), ttTomTat), h("label", { class: "f" }, h("span", {}, "Điểm nổi bật"), ttDiem),
      h("label", { class: "f" }, h("span", {}, "Khách mục tiêu"), ttKhach), h("label", { class: "f" }, h("span", {}, "Điều KHÔNG nói"), ttKhongNoi),
      h("label", { class: "f" }, h("span", {}, "Đọc từ ảnh"), ttTuAnh)),
    h("div", { class: "card" }, h("h3", {}, "Quy tắc và ghi nhớ"), h("label", { class: "f" }, h("span", {}, "Quy tắc"), quyTac), h("label", { class: "f" }, h("span", {}, "Ghi nhớ"), ghiNho)),
    loi,
    h("div", { class: "row wrap sp-nut" }, luuBtn, h("a", { class: "btn", href: veKichBan ? `#/kich-ban/${encodeURIComponent(veKichBan)}` : "#/san-pham", title: veKichBan ? "Quay lại kịch bản, không lưu" : "Về danh sách sản phẩm" }, veKichBan ? "Quay lại kịch bản" : "Danh sách"), h("span", { class: "grow" }), xoaBtn),
    kbDung);
  if (d && store.user?.role === "super" && d.ownerId === store.user.id) root.append(theSpMauNguon(d.id)); // Sản phẩm mẫu (san-pham-mau.ts)
  veTep(); veAnh(); veKbDung();
  if (!d) ten.focus();
  return () => {};
}
